import { useMemo, useState } from "react";
import FeedbackButton from "./FeedbackButton";
import { useAppStore } from "@/stores/appStore";
import { useMapStore } from "@/stores/mapStore";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "./ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";

const QUICK_LINKS = [
  {
    href: "https://data.azdhs.gov/data-explorers/epht-explorer",
    label: "AZ Environmental Public Health Tracking",
    target: "_blank",
  },
  {
    href: "https://data.azdhs.gov/data-explorers/community-profiles/home",
    label: "Community Profiles",
    target: "_blank",
  },
  {
    href: "https://data.azdhs.gov/reports-and-catalogs/metadata-catalog-and-data-profiles",
    label: "Metadata Catalog & Data Profiles",
    target: "_blank",
  },
  {
    href: "https://www.azdhs.gov/director/reports/index.php",
    label: "ADHS Reports",
    target: "_blank",
  },
  {
    href: "https://www.azdhs.gov/licensing/index.php#azcarecheck",
    label: "AZ Care Check",
    target: "_blank",
  },
  {
    href: "https://www.azdhs.gov/licensing/index.php#databases",
    label: "Licensing Provider & Facility Databases",
    target: "_blank",
  },
  {
    href: "https://data.azdhs.gov/data-assets/search-data",
    label: "Search Data Assets",
    target: "_blank",
  },
];

function QuickLinksDropdown() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={"outline"}
          className="text-primary-foreground quickLinkBtn"
        >
          Quick Links
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {QUICK_LINKS.map((link) => (
          <DropdownMenuItem key={link.href} asChild>
            <a
              href={link.href}
              target={link.target}
              rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
            >
              <span className="hdr-dropdown__chevron" aria-hidden="true">
                &rsaquo;
              </span>{" "}
              {link.label}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Same list of links, rendered as a submenu instead of its own
// top-level dropdown — used inside the mobile hamburger menu, where
// everything (including Quick Links) collapses into one menu.
function QuickLinksSubmenu() {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>Quick Links</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {QUICK_LINKS.map((link) => (
            <DropdownMenuItem key={link.href} asChild>
              <a
                href={link.href}
                target={link.target}
                rel={
                  link.target === "_blank" ? "noopener noreferrer" : undefined
                }
              >
                <span className="hdr-dropdown__chevron" aria-hidden="true">
                  &rsaquo;
                </span>{" "}
                {link.label}
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

interface HeaderProps {
  isSplitView?: boolean;
  isTourCompleted?: boolean;
  onToggleSplitView?: () => void;
  onCopyContentClick?: () => void;
  onStartTourClick?: () => void;
  handleDownloadUserGuide?: () => void;
}

export function Header({
  isSplitView = false,
  isTourCompleted = false,
  onToggleSplitView,
  onCopyContentClick,
  onStartTourClick,
  handleDownloadUserGuide,
}: HeaderProps) {
  const { apps, instances: appInstances } = useAppStore();
  const {
    actions: { setPlay },
    instances,
  } = useMapStore();
  const isMobile = useIsMobile();
  // Lifted out so the mobile hamburger's Feedback menu item can open
  // this dialog without nesting FeedbackButton's own trigger inside a
  // DropdownMenuItem (see FeedbackButton.tsx for why that's unsafe).
  // Unused on desktop, where FeedbackButton keeps its own internal
  // state and trigger exactly as before.
  const [mobileFeedbackOpen, setMobileFeedbackOpen] = useState(false);

  const eligibleApps = useMemo(() => {
    if (apps.length < 2) return [];

    return apps.filter((appId) => {
      const { playVar } = instances[appId] ?? {};
      const year = appInstances[appId]?.query?.year ?? [];
      return year.length !== 1 && !playVar;
    });
  }, [apps, instances, appInstances]);

  const showStartSlider = useMemo(() => {
    let show = false;
    for (let i = 0; i < apps?.length; i++) {
      const id = apps[i];
      if (appInstances?.[id]?.query?.year?.length > 1) {
        show = true;
        break;
      }
    }
    return show;
  }, [apps, appInstances]);

  function handlePlay() {
    eligibleApps.forEach((appId) => setPlay(appId, true));
  }

  return (
    <header className="bg-primary h-16 flex flex-row flex-nowrap justify-between items-center">
      <div className="flex flex-row space-x-2 pl-4 items-center">
        <a
          href="https://data.azdhs.gov"
          className="text-primary-foreground"
          aria-label="Home"
          title="Home"
        >
          <span className="fa-solid fa-house text-lg" />
        </a>
        <img
          src="/images/data-explorer-white.png"
          alt="Arizona Data Explorer"
          className="w-auto h-8"
        />
      
      </div>

      {isMobile ? (
        <nav className="flex flex-row items-center pr-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size={"sm"}
                variant={"outline"}
                className="text-primary-foreground"
                aria-label="Open menu"
              >
                <span className="fa-solid fa-bars" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isSplitView && !isTourCompleted && (
                <DropdownMenuItem onClick={onStartTourClick}>
                  Tour
                </DropdownMenuItem>
              )}

              <DropdownMenuItem onClick={handleDownloadUserGuide}>
                User Guide
              </DropdownMenuItem>

              {isSplitView && (
                <DropdownMenuItem onClick={onCopyContentClick}>
                  Copy Content
                </DropdownMenuItem>
              )}
              {isSplitView && showStartSlider && (
                <DropdownMenuItem onClick={handlePlay}>
                  Start Timeslider
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onToggleSplitView}>
                {isSplitView ? "Remove Map" : "Add Map"}
              </DropdownMenuItem>

              <QuickLinksSubmenu />

              <DropdownMenuItem asChild>
                <a href="https://data.azdhs.gov/need-help/home" target="_blank">
                  About
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  href="https://geodata-adhsgis.hub.arcgis.com/pages/story-map"
                  target="_blank"
                >
                  Data Stories
                </a>
              </DropdownMenuItem>

              <DropdownMenuItem
                onSelect={(e) => {
                  // Prevent the menu's default close-on-select from
                  // racing the dialog open — the dialog itself lives
                  // outside this menu (below), so this only needs to
                  // flip the external open flag.
                  e.preventDefault();
                  setMobileFeedbackOpen(true);
                }}
              >
                Feedback
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Rendered outside the DropdownMenu tree entirely, so it
              isn't subject to the menu's own mount/unmount lifecycle. */}
          <FeedbackButton
            open={mobileFeedbackOpen}
            onOpenChange={setMobileFeedbackOpen}
            hideTrigger
          />
        </nav>
      ) : (
        <nav className="flex flex-row space-x-2 items-center pr-2">
          {!isSplitView && !isTourCompleted && (
            <Button
              size={"sm"}
              variant={"outline"}
              className="text-primary-foreground"
              onClick={onStartTourClick}
            >
              {"Tour"}
            </Button>
          )}

          <Button
            size={"sm"}
            variant={"outline"}
            className="text-primary-foreground userGuideBtn"
            onClick={handleDownloadUserGuide}
          >
            {"User Guide"}
          </Button>

          {isSplitView && (
            <Button
              variant={"outline"}
              className="text-primary-foreground "
              onClick={onCopyContentClick}
            >
              {"Copy Content"}
            </Button>
          )}
          {isSplitView && showStartSlider && (
            <Button
              variant={"outline"}
              className="text-primary-foreground"
              onClick={handlePlay}
            >
              Start Timeslider
            </Button>
          )}
          <Button
            variant={"outline"}
            className="text-primary-foreground addMapBtn"
            onClick={onToggleSplitView}
          >
            {isSplitView ? "Remove Map" : "Add Map"}
          </Button>
          <QuickLinksDropdown />
          <Button
            variant={"outline"}
            className="text-primary-foreground aboutBtn"
            asChild
          >
            <a href="https://data.azdhs.gov/need-help/home" target="_blank">
              About
            </a>
          </Button>
          <Button
            asChild
            variant={"outline"}
            className="text-primary-foreground dataStoriesBtn"
          >
            <a
              href="https://geodata-adhsgis.hub.arcgis.com/pages/story-map"
              target="_blank"
            >
              Data Stories
            </a>
          </Button>
          <FeedbackButton />
        </nav>
      )}
    </header>
  );
}
